import React, { Fragment } from 'react';
import Card from './Card';
import { Box, Grid, Link, Typography } from '@mui/material';
import { formatAsDollars } from '../../utils/formatters';
import Tooltip from './Tooltip';
import NavButton from './NavButton';

const BILL_SAVINGS_DISCLAIMER =
  "Disclaimer: Bill savings are calculated based on single-family residences in California. Actual savings are highly variable based on usage, building characteristics, and rate plans. Savings data is sourced from the National Renewable Energy Laboratory's State Level Residential Building Stock and Energy Efficiency & Electrification Packages Analysis.";

const TREES_PLANTED_EXPLANATION =
  'Upgrading your appliances reduces CO₂ emissions. Trees naturally absorb CO₂ from the atmosphere, so we use this to visualize your impact. The number listed is how many trees it would take to absorb the same amount of CO₂ your upgrades save.';

const explainTotal = ({ electric, naturalGas, gasoline }) => {
  const describeUsage = (name, average) => {
    return `${average < 0 ? 'a decrease' : 'an increase'}
      in ${name} usage
      of ${formatAsDollars(Math.abs(average))} per year`;
  };

  const parts = [
    naturalGas && describeUsage('PG&E gas', naturalGas),
    gasoline && describeUsage('gasoline', gasoline),
    electric && describeUsage('PG&E electric', electric),
  ].filter(Boolean);

  if (parts.length === 0) {
    // This would only happen if there was
    // about $0 of savings for each of electric,
    // gasoline, and natural gas.
    return null;
  }

  const combined =
    parts.length === 3 ? `${parts[0]}, ${parts[1]}, and ${parts[2]}` : parts.join(' and ');

  return `The estimated average energy bill impact consists of ${combined.trim()}. Here's the energy bill impact per product:`;
};

export default function BillImpactStep({ myProject, onBack, onNext }) {
  const roomAnnualCost = (room) => {
    return (
      room.annual_cost['naturalGas'] + room.annual_cost['gasoline'] + room.annual_cost['electric']
    );
  };

  const sumAnnualCost = (field) => {
    // Note that this returns null if all
    // rooms have null for field
    return myProject
      .map((room) => room.annual_cost[field])
      .filter(Boolean)
      .reduce((acc, n) => (acc || 0) + n, null);
  };

  const annualCosts = {
    naturalGas: sumAnnualCost('naturalGas'),
    gasoline: sumAnnualCost('gasoline'),
    electric: sumAnnualCost('electric'),
  };

  const totalSavings =
    (annualCosts.naturalGas || 0) + (annualCosts.gasoline || 0) + (annualCosts.electric || 0);

  const totalExplanation = explainTotal(annualCosts);

  const totalCo2Emissions = myProject
    .map((room) => room.co2_emission)
    .filter(Boolean)
    .reduce((acc, val) => acc + val, 0);
  // Emissions are negative, so divide by a negative
  const treesPlanted = totalCo2Emissions / -25;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={7}>
          <Card>
            <Box display="flex" mb={1}>
              <Typography variant="h3" fontWeight={600}>
                Energy bill savings
              </Typography>
              <Tooltip
                title={
                  <>
                    This value is the total estimated impact on your annual energy bills for
                    electricity, natural gas, and other fuels. The cost change is variable depending
                    on the characteristics of your home, such as insulation, existing appliance
                    efficiency, home vintage (age), energy usage, and number of occupants, as well
                    as the rate plan you&apos;re enrolled in and the time-of-day for your energy
                    usage.
                    <br />
                    <br />
                    To estimate savings based on your home and usage, use the{' '}
                    <Link
                      href="https://www.example.com/clean-energy-calculator"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'common.white', textDecorationColor: '#FFF' }}
                    >
                      Clean Energy Calculator
                    </Link>
                    .
                  </>
                }
              />
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography
                variant="body1"
                fontSize="2.25rem"
                fontWeight={700}
                color={totalSavings <= 0 ? 'green.400' : 'red.400'}
              >
                {formatAsDollars(totalSavings, { explicitPlus: true })}
              </Typography>
              <Typography variant="body1" fontSize="0.875rem" fontWeight={700} ml={0.5}>
                /year
              </Typography>
            </Box>
            <Box display="flex" mb={2}>
              <Box display="flex" alignItems="center" mr={2}>
                <Box height="14px" width="14px" bgcolor="green.400" borderRadius="50%" />
                <Typography variant="body2" ml={0.5}>
                  = Bill Savings
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Box height="14px" width="14px" bgcolor="red.400" borderRadius="50%" />
                <Typography variant="body2" ml={0.5}>
                  = Bill Increase
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" mb={2}>
              {totalExplanation}
              <Tooltip title={BILL_SAVINGS_DISCLAIMER} />
            </Typography>

            <Grid container spacing={0} pr={2}>
              {myProject.map((room, idx) => (
                <Fragment key={room.handle}>
                  <Grid
                    item
                    xs={6}
                    sm={8}
                    display="flex"
                    alignItems="center"
                    borderBottom={idx < myProject.length - 1 ? '1px dotted' : 'none'}
                    borderColor="grey.400"
                  >
                    <Typography variant="h5" fontWeight={600}>
                      {room.title}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    py={1.25}
                    px={{ xs: 0, sm: 2 }}
                    borderBottom={idx < myProject.length - 1 ? '1px dotted' : 'none'}
                    borderColor="grey.400"
                  >
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      textAlign={{ xs: 'right', sm: 'center' }}
                    >
                      {formatAsDollars(roomAnnualCost(room), { explicitPlus: true })}
                      <Typography variant="body2" component="span" ml={0.5}>
                        /year
                      </Typography>
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12} sm={5}>
          {treesPlanted > 0 && (
            <Box>
              <Card>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Typography variant="h4" fontWeight={600}>
                      Trees planted per year
                    </Typography>
                    <Tooltip title={TREES_PLANTED_EXPLANATION} />
                  </Box>
                  <Typography variant="h5" fontWeight={700}>
                    {Math.round(treesPlanted)}
                  </Typography>
                </Box>
              </Card>
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={6}>
          <NavButton onClick={onBack} variant="secondary">
            Go Back
          </NavButton>
        </Grid>
        <Grid item xs={6}>
          <NavButton onClick={onNext} variant="primary">
            Next Steps
          </NavButton>
        </Grid>
      </Grid>
    </Box>
  );
}
